/**
 * Risk appetite over the session, rather than at one instant.
 *
 * ## Why a history exists at all
 *
 * `riskSpread` answers "what is the market doing right now". The Fundamentals
 * screen needs "what has the market been doing", because the useful reading is
 * not +0.9 — it is +0.9 *and rising since the ECB spoke*. A single instant
 * cannot show a turn, and a turn is the thing a trader is watching for.
 *
 * ## Why it is a ring buffer and not a table
 *
 * This is a derived view of prices the system already has. It is rebuildable
 * from bars at any time, nobody bills against it, and no decision is defended
 * with it — so persisting it would buy durability nothing needs and cost a
 * migration, a retention policy and a table that grows forever.
 *
 * The cost of the choice is honest and bounded: **the series restarts empty
 * when the API restarts.** A user watching across a deploy sees the ribbon
 * begin again. That is visible, self-healing within one session, and cheap —
 * unlike a silently-growing table nobody prunes.
 *
 * Pure, per `packages/core` convention. The clock is passed in.
 */

/** One reading, kept small — a chart needs the shape, not the legs. */
export interface AppetitePoint {
  readonly atMs: number;
  /** Null when too few legs were trustworthy. Charts must draw a GAP, not a zero. */
  readonly appetite: number | null;
  /** Null whenever `appetite` is. Below ~0.7 the mean describes nothing. */
  readonly coherence: number | null;
  /** How many legs the reading rested on. */
  readonly counted: number;
}

export interface AppetiteHistory {
  readonly points: readonly AppetitePoint[];
  /** The oldest point that would still be kept, ms. Points before this are gone. */
  readonly retainMs: number;
}

export const EMPTY_HISTORY: AppetiteHistory = { points: [], retainMs: 0 };

/**
 * Append a reading and drop anything past the retention horizon.
 *
 * Returns a NEW history; the input is never mutated. The service holding this
 * hands the same object to concurrent requests, and a buffer that mutates in
 * place would let one request observe a half-updated series.
 *
 * ## Out-of-order readings are rejected, not sorted
 *
 * A point at or before the newest one is dropped. Two callers racing on the
 * same clock tick, or a clock stepping backwards under NTP, would otherwise
 * write a series that doubles back on itself — and an x-axis that goes
 * backwards draws a line through the middle of the chart that means nothing.
 * Dropping loses at most one reading of a series sampled continuously.
 */
export function appendAppetite(
  history: AppetiteHistory,
  point: AppetitePoint,
  retentionMs: number,
): AppetiteHistory {
  const newest = history.points[history.points.length - 1];
  if (newest !== undefined && point.atMs <= newest.atMs) return history;

  const retainMs = point.atMs - Math.max(1, retentionMs);
  const points = [...history.points, point].filter((p) => p.atMs >= retainMs);
  return { points, retainMs };
}

/**
 * The window of history a caller asked for.
 *
 * Separate from retention on purpose: the buffer may hold four hours while a
 * screen asks for one. A screen that always gets everything cannot show less
 * without discarding data it already paid to transfer.
 */
export function appetiteSince(history: AppetiteHistory, sinceMs: number): readonly AppetitePoint[] {
  return history.points.filter((p) => p.atMs >= sinceMs);
}

/**
 * Which way the reading has moved across the points given.
 *
 * Compares the mean of the first third against the mean of the last third
 * rather than first-vs-last, because endpoints are single samples and a series
 * this short is noisy — one outlier at either end would otherwise decide the
 * whole verdict.
 *
 * Returns `null` when there is nothing defensible to say: fewer than three
 * readable points, or a move too small to distinguish from noise. Callers
 * render null as "steady" and must not fabricate a direction from it.
 */
export function appetiteTrend(
  points: readonly AppetitePoint[],
  minMove = 0.15,
): 'rising' | 'falling' | null {
  const usable = points.filter((p) => p.appetite !== null);
  if (usable.length < 3) return null;

  const third = Math.max(1, Math.floor(usable.length / 3));
  const mean = (xs: readonly AppetitePoint[]): number =>
    xs.reduce((a, p) => a + (p.appetite ?? 0), 0) / xs.length;

  const move = mean(usable.slice(-third)) - mean(usable.slice(0, third));
  if (Math.abs(move) < minMove) return null;
  return move > 0 ? 'rising' : 'falling';
}
